
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface SplashScreenProps {
  onFinish: () => void;
  duration?: number;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ 
  onFinish, 
  duration = 10000 // 10 seconds by default
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(1); // Control animation phases

  useEffect(() => {
    // Animation phase transitions
    const phase2Timer = setTimeout(() => {
      setAnimationPhase(2); // Transition to slower animation after 3 seconds
    }, 3000);
    
    // Start the exit animation after duration - 1s
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 1000);

    // Call onFinish after the full duration
    const finishTimer = setTimeout(() => {
      onFinish();
    }, duration);

    return () => {
      clearTimeout(phase2Timer);
      clearTimeout(exitTimer);
      clearTimeout(finishTimer);
    };
  }, [duration, onFinish]);

  return (
    <motion.div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-nalc-dark cyber-backdrop ${isExiting ? 'animate-fade-out' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        className="cyber-card p-8 w-80 md:w-96 relative overflow-hidden cyber-glow card-glow-intense splash-card-pulse"
        initial={{ rotateY: 0 }}
        animate={{ 
          rotateY: [0, 180, 360, 540, 720, 900, 1080],
          rotateX: [0, 15, 0, -15, 0, 15, 0],
          scale: [1, 1.05, 1, 1.05, 1]
        }}
        transition={{ 
          duration: 10,
          times: animationPhase === 1 
            ? [0, 0.1, 0.2, 0.3, 0.4, 0.5, 1]  // Fast during first 3 seconds
            : [0, 0.3, 0.5, 0.7, 0.8, 0.9, 1], // Slower after 3 seconds
          ease: "easeInOut"
        }}
        style={{ transformStyle: 'preserve-3d', perspective: '800px' }}
      >
        <div className="cyber-corners-tl"></div>
        <div className="cyber-corners-tr"></div>
        <div className="cyber-corners-bl"></div>
        <div className="cyber-corners-br"></div>
        
        <div className="flex flex-col items-center justify-center text-white">
          <motion.div
            className="logo-container w-56 h-56 mb-8 relative"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <div className="absolute inset-0 bg-[#00c3ff] opacity-30 blur-3xl rounded-full cyber-glow"></div>
            <motion.img 
              src="/lovable-uploads/1dd5a01c-f447-44fa-821e-b0951b6ade29.png" 
              alt="NALC Logo" 
              className="w-full h-full object-contain"
              style={{ transformStyle: 'preserve-3d' }}
              animate={animationPhase === 1 ? {
                rotateY: [0, 360],
                rotateX: [0, 20, 0, -20, 0],
                scale: [1, 1.1, 1],
                rotateZ: [0, 20, 0, -20, 0]
              } : {
                rotateY: [0, 360],
                rotateX: [0, 10, 0, -10, 0],
                scale: [1, 1.05, 1],
                rotateZ: [0, 10, 0, -10, 0]
              }}
              transition={animationPhase === 1 ? {
                duration: 3,
                ease: "easeInOut",
                repeat: 1,
                repeatDelay: 0
              } : {
                duration: 7,
                ease: "easeInOut",
                repeat: Infinity,
                repeatDelay: 0.2
              }}
            />
          </motion.div>

          <motion.div
            className="text-center space-y-4 relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="absolute inset-0 bg-[#00c3ff] opacity-20 blur-xl rounded-full -z-10"></div>
            <h1 className="text-4xl font-bold cyber-text-glow">Noor Ali Learning Center</h1>
            <p className="text-xl text-cyan-200">Student Program & Fee Tracker</p>
            <div className="flex justify-center space-x-2 pt-4">
              <div className="w-3 h-3 rounded-full bg-cyan-400 animate-pulse"></div>
              <div className="w-3 h-3 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-3 h-3 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SplashScreen;
